import { useEffect, useCallback, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useStoreContext } from '../../store/store';
import { routePermissions } from '../../routes/routePermissions';
import { Helpers, StorageService } from '../helpers';
import { Menu } from '../constants/enums';
import { Permissions } from '../constants/permissions';

export const useAuthorization = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, actions, dispatch } = useStoreContext();

  const anonymousAccess: string[] = [
    '/login',
    '/first-time-setup',
    '/forgot-password',
    '/mfa-login',
    '/create-account'
  ];
  const allowAnonymousAccess = anonymousAccess.some((x) => x === location.pathname);

  const isDevToolsPage = location.pathname.startsWith('/dev-tools');
  const expireTime = user.expiresAt?.getTime() ?? 0;
  const currentTime = Helpers.getUtcDate().getTime();
  const isAuthenticated =
    user && !Helpers.isNullOrWhiteSpace(user.token) && expireTime >= currentTime;

  // Helper function to check if user has permission for a specific route
  // Uses OR logic - user needs ANY of the required permissions (plus SYSADMIN always has access)
  const hasRoutePermission = useCallback(
    (routePath: string, routeRequiredPermissions: string[]): boolean => {
      // Ensure permissions is an array
      const userPermissions = user.permissions || [];

      // Check basic APP_USER permission for most routes (users must be logged in)
      if (!userPermissions.includes(Permissions.APP_USER)) {
        return false;
      }

      // SYSADMIN has access to everything
      if (userPermissions.includes(Permissions.SYSADMIN)) {
        return true;
      }

      // Permission logic based on route
      switch (routePath) {
        case '/members':
        case '/members/:clubMemberId':
          // Members: MEMBER_READ_ONLY or MEMBER_UPDATE
          return (
            userPermissions.includes(Permissions.MEMBER_READ_ONLY) ||
            userPermissions.includes(Permissions.MEMBER_UPDATE)
          );

        case '/members/add':
          // Add New Member: MEMBER_UPDATE
          return userPermissions.includes(Permissions.MEMBER_UPDATE);

        case '/users':
        case '/users/:userId':
        case '/codes':
        case '/codes/:systemCodeId':
        case '/membership-types':
        case '/membership-types/:membershipTypeId':
        case '/files':
          // System Utilities & User Privileges: SYSADMIN only (already checked above)
          return false;

        case '/reports/membership-report':
        case '/reports/payment-report':
        case '/reports/work-hours-report':
        case '/':
          // All Reports & Dashboard: Any logged in user
          // Note: EDITING work hours requires WORKHOURS_UPDATE, but VIEWING is open
          return true;

        case '/billing/invoice-report':
          // Invoice Report: MEMBER_UPDATE
          return userPermissions.includes(Permissions.MEMBER_UPDATE);

        case '/billing/invoices':
          // Manage Invoices: GEN_INVOICE
          return userPermissions.includes(Permissions.GEN_INVOICE);

        default:
          // Default: user needs ANY of the required permissions (OR logic)
          return routeRequiredPermissions.some((p) => userPermissions.includes(p));
      }
    },
    [user?.permissions]
  );

  const sidebarMenu = useMemo(
    () =>
      routePermissions
        .filter((route) => {
          if (!Helpers.hasFlag(route.menu, Menu.SIDEBAR)) {
            return false;
          }
          return hasRoutePermission(route.path, route.requiredPermissions);
        })
        .map((route) => {
          // Filter children based on permissions
          if (route.children && route.children.length > 0) {
            const filteredChildren = route.children.filter((child) =>
              hasRoutePermission(child.path, child.requiredPermissions)
            );
            // Only show parent if it has visible children
            if (filteredChildren.length === 0) {
              return null;
            }
            return { ...route, children: filteredChildren };
          }
          return route;
        })
        .filter((route): route is NonNullable<typeof route> => route !== null),
    [hasRoutePermission]
  );

  const profileMenu = useMemo(
    () =>
      routePermissions.filter((route) => {
        return (
          Helpers.hasFlag(route.menu, Menu.PROFILE) &&
          route.requiredPermissions.every((p) => (user.permissions || []).includes(p))
        );
      }),
    [user?.permissions]
  );

  const logout = useCallback(
    (reason?: number | undefined) => {
      StorageService.setAuthToken('');
      dispatch(actions.setUserLoggedOut());
      void navigate('/login', { state: { returnPath: '/', reason } });
    },
    [actions, dispatch, navigate]
  );

  useEffect(() => {
    // on dev tools page, but not running local
    if (isDevToolsPage && !Helpers.isRunningLocal()) {
      void navigate('/not-authorized');
    }

    // on login or related page, but already authenticated, send to dashboard
    if (allowAnonymousAccess && isAuthenticated) {
      void navigate('/');
      return;
    }

    // not on login page and not authenticated, send to login page
    if (!allowAnonymousAccess && !isAuthenticated) {
      void navigate('/login', { state: { returnPath: location.pathname + location.search } });
      return;
    }

    // check page permissions
    const currentRoute = routePermissions.find((route) => route.path === location.pathname);
    if (currentRoute) {
      const hasRequiredPermissions = hasRoutePermission(
        currentRoute.path,
        currentRoute.requiredPermissions
      );
      if (!hasRequiredPermissions) {
        void navigate('/not-authorized?permissions');
      }
    }
  }, [
    user,
    navigate,
    allowAnonymousAccess,
    location.pathname,
    location.search,
    isAuthenticated,
    isDevToolsPage,
    hasRoutePermission
  ]);

  return {
    sidebarMenu,
    profileMenu,
    logout,
    user
  };
};
